// SD-0024 (14/10/2026) — "Enviar todos os botões de WhatsApp para o número da imobiliária".
//
// Opção do CRM (Personalizar site › Configurações), desligada por padrão, lida do banco novo
// (`sobressai_novo.site_layout_opcoes`) e entregue pelo site-graphql como
// `site.whatsapp_tudo_imobiliaria`. Ligada, todo botão de WhatsApp que abriria o número de uma
// PESSOA (corretor da equipe, corretor dos exclusivos, agenciador do imóvel) passa a abrir o
// WhatsApp da IMOBILIÁRIA, e a mensagem leva o nome do corretor + um CÓDIGO INVISÍVEL que o
// worker do CRM lê para entregar o lead direto a esse corretor.
//
// CONTRATO v2 (14/10 — a tag visível "[WS-E123]" da v1 foi APAGADA pelo lead num teste real, e o
// lead caiu na roleta; o worker ainda aceita a v1 na transição, mas o site só escreve a v2):
//
//     código = U+2060 + dígitos base-3 de N + U+2060
//       alfabeto: U+200B = 0 · U+200C = 1 · U+200D = 2 (mais significativo primeiro)
//       N = b·10^10 + id
//         b  = botão: E=1 cards da equipe na home · V=2 card do corretor em /exclusivos
//                     I=3 página do imóvel (flutuante, galerias 3/4, rodapé, detalhes) · L=4 lista de imóveis
//         id = tbl_parceiro.id do corretor/agenciador cujo telefone o botão usaria (1 a 10 dígitos)
//     regex do worker (1ª ocorrência): /\u2060([\u200B\u200C\u200D]{1,40})\u2060/
//
//   Posição: COLADO no nome do corretor, ANTES da pontuação final (no fim da mensagem o WhatsApp
//   corta o "espaço" invisível):
//     E/V: "Olá! Acabei de acessar o site da {empresa} e gostaria de falar com {nome}{código}."
//     I/L: "{texto de hoje}\n\nGostaria de falar com {nome}{código}."
//   Sem nome: "… falar com um corretor{código}." (E/V) · "Gostaria de falar com o corretor{código}." (I/L).
//   O texto inteiro vai por encodeURIComponent: o código viaja na URL como %E2%81%A0, %E2%80%8B…
//
// ⚠️ Nada de "cod/código/ref/referência/imóvel + número" no texto: casaria com o detector de
// referência de imóvel do worker (wa-property-ref.util.ts). O código invisível não tem dígito.
//
// REGRA DE OURO: as duas funções recebem a URL que o botão abriria HOJE e, com a opção desligada
// (ou site sob medida, abaixo), devolvem essa MESMA string — href e texto byte a byte iguais aos
// de hoje. Provado em scripts/test-whatsapp-imobiliaria.mjs (unitário + "desembrulhar" os
// arquivos tocados e comparar com o original).

export type BotaoWhatsSite = 'E' | 'V' | 'I' | 'L';

// Sites com WhatsApp feito sob medida — a regra deles fica ACIMA da opção e não muda:
// 2524 redirecionador externo (utils/site2524WhatsApp.ts) · 1791 telefones fixos por transação ·
// 1892 botão do corretor com número fixo (DetailsPiecesProperties) · 2551 locação com número fixo ·
// 1937 card da equipe sem DDI e sem texto · 1803 card da equipe sem WhatsApp.
export const SITES_WHATSAPP_SOB_MEDIDA: ReadonlySet<number> = new Set([2524, 1791, 1892, 2551, 1937, 1803]);

/** A opção vale para este site? (ligada no CRM e site fora da lista sob medida) */
export const whatsTudoImobiliariaAtivo = (site: any): boolean =>
    site?.whatsapp_tudo_imobiliaria === true && !SITES_WHATSAPP_SOB_MEDIDA.has(Number(site?.id));

const soDigitos = (valor: unknown): string => String(valor ?? '').replace(/\D/g, '');

/**
 * Telefone da imobiliária pela MESMA regra do botão flutuante sem agenciador
 * (WhatsappComponent: tel1 → tel2 → tel3 → tel4; o 2537 começa pelo tel3). Só dígitos, sem DDI.
 */
export const telefoneDaImobiliaria = (site: any): string => {
    const dados = site?.Dados || {};
    const telefone =
        Number(site?.id) === 2537
            ? dados.tel3 || dados.tel1 || dados.tel2 || dados.tel4
            : dados.tel1 || dados.tel2 || dados.tel3 || dados.tel4;
    return soDigitos(telefone);
};

// ─── código invisível (contrato v2) ───
const DELIMITADOR = '\u2060'; // WORD JOINER
const ALFABETO = ['\u200B', '\u200C', '\u200D']; // ZWSP=0 · ZWNJ=1 · ZWJ=2
const VALOR_DO_BOTAO: Record<BotaoWhatsSite, number> = { E: 1, V: 2, I: 3, L: 4 };
const BOTAO_DO_VALOR: BotaoWhatsSite[] = ['E', 'V', 'I', 'L']; // índice = valor - 1
const DEZ_A_DEZ = 1e10; // N = b·10^10 + id (máx. 49.999.999.999 — inteiro exato em Number)
const RE_CODIGO_INVISIVEL = /\u2060([\u200B\u200C\u200D]{1,40})\u2060/;
const RE_CARACTERES_DO_CODIGO = /[\u2060\u200B\u200C\u200D]/g;

const idValido = (id: unknown): number => {
    const numero = Number(id);
    return Number.isInteger(numero) && numero > 0 && String(numero).length <= 10 ? numero : 0;
};

/**
 * Código invisível do botão `botao` para o corretor `id` (tbl_parceiro.id): U+2060 + N em base 3
 * (U+200B/U+200C/U+200D) + U+2060. '' quando o id não é válido (1 a 10 dígitos).
 */
export const codigoInvisivel = (botao: BotaoWhatsSite, id: unknown): string => {
    const numero = idValido(id);
    const b = VALOR_DO_BOTAO[botao];
    if (!numero || !b) return '';
    let n = b * DEZ_A_DEZ + numero;
    let digitos = '';
    while (n > 0) {
        digitos = ALFABETO[n % 3] + digitos;
        n = Math.floor(n / 3);
    }
    return `${DELIMITADOR}${digitos}${DELIMITADOR}`;
};

/**
 * Lê o 1º código invisível de `texto` (mesma regra do worker). `null` se não houver, ou se o valor
 * não for um botão E/V/I/L + id válido. Exportada para o teste e para documentar o contrato.
 */
export const decodificaCodigoInvisivel = (texto: unknown): { botao: BotaoWhatsSite; id: number } | null => {
    const m = RE_CODIGO_INVISIVEL.exec(String(texto ?? ''));
    if (!m) return null;
    let n = 0;
    for (let i = 0; i < m[1].length; i++) {
        n = n * 3 + ALFABETO.indexOf(m[1].charAt(i));
        if (n > Number.MAX_SAFE_INTEGER) return null;
    }
    const botao = BOTAO_DO_VALOR[Math.floor(n / DEZ_A_DEZ) - 1];
    const id = n % DEZ_A_DEZ;
    return botao && id > 0 ? { botao, id } : null;
};

/** O texto como o lead VÊ: sem os caracteres do código invisível. */
export const semCodigoInvisivel = (texto: string): string => String(texto ?? '').replace(RE_CARACTERES_DO_CODIGO, '');

type Desvio = { telefone: string; codigo: string; nome: string };

// Últimos 8 dígitos: casa (51) 98195-7676 com 5551981957676 e com o 555181957676 do WhatsApp (sem o 9).
const finalDoTelefone = (valor: unknown): string => soDigitos(valor).slice(-8);

/**
 * A "pessoa" usa um número DA PRÓPRIA imobiliária (tel1..tel4)? Ex.: cs2437, agenciador de 106 imóveis =
 * conta da empresa "Conceito Lar Imóveis" com o mesmo número de Vendas. Aí o botão JÁ abre a imobiliária e não
 * há corretor escolhido: sem código (senão o worker entregaria o lead à conta da empresa, pulando a roleta).
 */
const pessoaUsaNumeroDaImobiliaria = (site: any, pessoa: any): boolean => {
    const dados = site?.Dados || {};
    const daImobiliaria = new Set(
        [dados.tel1, dados.tel2, dados.tel3, dados.tel4].map(finalDoTelefone).filter((t) => t.length === 8),
    );
    const daPessoa = finalDoTelefone(pessoa?.telefone || pessoa?.telefone_2 || pessoa?.telefone_3);
    return daPessoa.length === 8 && daImobiliaria.has(daPessoa);
};

// Decide se o botão desvia: opção ativa + pessoa com id e telefone PRÓPRIO + imobiliária com telefone.
const desvio = (site: any, pessoa: any, botao: BotaoWhatsSite): Desvio | null => {
    if (!whatsTudoImobiliariaAtivo(site)) return null;
    if (!pessoa || !(pessoa.telefone || pessoa.telefone_2 || pessoa.telefone_3)) return null;
    if (pessoaUsaNumeroDaImobiliaria(site, pessoa)) return null;
    const codigo = codigoInvisivel(botao, pessoa.id);
    if (!codigo) return null;
    const telefone = telefoneDaImobiliaria(site);
    if (!telefone) return null;
    return { telefone: `55${telefone}`, codigo, nome: String(pessoa.nome || '').replace(/\s+/g, ' ').trim() };
};

// Decodifica percent-encoding sem lançar (os links do projeto misturam texto cru, encodado e
// híbrido; "%" solto e legítimo — "50%" — derruba o decodeURIComponent). Mesmo critério de
// utils/urlPaginaNoWhats.ts.
const decodificaSuave = (valor: string): string => {
    try {
        return decodeURIComponent(valor);
    } catch {
        /* tem % solto — decodifica só as sequências %XX válidas */
    }
    return valor.replace(/(?:%[0-9A-Fa-f]{2})+/g, (trecho) => {
        try {
            return decodeURIComponent(trecho);
        } catch {
            return trecho;
        }
    });
};

const RE_LINK_WHATS_COM_DESTINO = /^(?:https?:)?\/\/(?:api\.whatsapp\.com\/send\?(?:[^#]*&)?phone=|wa\.me\/\+?\d)/i;
const RE_OUTRO_PARAM = /&(?:phone|app_absent|type|source|data|lang)=/i;

/**
 * Botões de I (página do imóvel) e L (lista): recebe a URL que o botão abriria HOJE e, se a opção
 * estiver ativa e o telefone for de `pessoa`, troca o destinatário pelo da imobiliária e acrescenta
 * "\n\nGostaria de falar com <nome><código>." à mensagem. Qualquer outro caso devolve `url` INTACTA
 * (opção desligada, site sob medida, pessoa nula, link que não é WhatsApp — '#', redirecionador,
 * agenda do Google…).
 *
 * `pessoa` só deve ser passada quando o telefone usado pelo botão é o dela (o chamador conhece as
 * exceções locais, ex.: número fixo do 2155 na venda, tel1 do 2455).
 */
export function whatsParaImobiliaria<T>(url: T, site: any, pessoa: any, botao: BotaoWhatsSite): T {
    const d = desvio(site, pessoa, botao);
    if (!d || typeof url !== 'string' || !RE_LINK_WHATS_COM_DESTINO.test(url.trim())) return url;
    let alvo: string = url;
    alvo = /^(?:https?:)?\/\/wa\.me\//i.test(alvo)
        ? alvo.replace(/(wa\.me\/)\+?\d*/i, `$1${d.telefone}`)
        : alvo.replace(/([?&]phone=)[^&#]*/i, `$1${d.telefone}`);
    const sufixo = `Gostaria de falar com ${d.nome || 'o corretor'}${d.codigo}.`;
    const marca = /([?&])text=/i.exec(alvo);
    if (!marca) {
        return `${alvo}${alvo.includes('?') ? '&' : '?'}text=${encodeURIComponent(sufixo)}` as unknown as T;
    }
    const inicio = marca.index + marca[0].length;
    const cabeca = alvo.slice(0, inicio);
    let cauda = alvo.slice(inicio);
    let resto = '';
    const corte = cauda.search(RE_OUTRO_PARAM);
    if (corte >= 0) {
        resto = cauda.slice(corte);
        cauda = cauda.slice(0, corte);
    }
    // Reencoda a mensagem inteira: "#", "&" e "?" do texto cru deixam de truncar o link (e o código).
    const mensagem = decodificaSuave(cauda);
    const nova = mensagem ? `${mensagem}\n\n${sufixo}` : sufixo;
    return `${cabeca}${encodeURIComponent(nova)}${resto}` as unknown as T;
}

/**
 * Cards da equipe (E: CardManager, CardManager1, ManagerGrid, Equipe Aurora/Horizonte/Panorama ·
 * V: card do corretor em /exclusivos):
 * recebe o link que o card abriria HOJE; com a opção ativa devolve o link do WhatsApp da
 * imobiliária com o texto do contrato, senão devolve `url` INTACTA.
 */
export function whatsCardEquipeImobiliaria<T>(url: T, site: any, pessoa: any, botao: 'E' | 'V'): T | string {
    const d = desvio(site, pessoa, botao);
    // sem link hoje (card sem telefone) = sem botão: a opção não cria botão novo
    if (!d || !url) return url;
    const empresa = String(site?.Dados?.empresa || '').trim() || 'imobiliária';
    const texto = `Olá! Acabei de acessar o site da ${empresa} e gostaria de falar com ${d.nome || 'um corretor'}${d.codigo}.`;
    return `https://api.whatsapp.com/send?phone=${d.telefone}&text=${encodeURIComponent(texto)}`;
}
